import * as React from 'react';
import * as _ from 'lodash';
import { useDispatch } from 'react-redux';
import {
  API,
  ContentLoading,
  DomainCName,
  downloadTextFile,
  EmailConfiguration,
  Logging,
  Notification,
  NotificationData,
  ResponseError,
} from '@vcd-ecm-ui/Common';
import { VtxButton } from '@vertexinc/vtx-ui-react-component-library';
import Step3Props from './Step3Props.interface';
import Footer from '../Footer/Footer';
import '../../../common-correspondence.scss';
import '../../configuration.scss';

const Step3 = ({
  gatewayEndPoint,
  configuration,
  onBack,
  onCancel,
  onNext,
}: Step3Props): JSX.Element => {
  const [cNameRecords, setcNameRecords] = React.useState(null);
  const [cNameRecordsError, setcNameRecordsError] = React.useState(false);
  const [notification, setNotification] = React.useState(null);

  const dispatch = useDispatch();

  const saveConfiguration = (): void => {
    const copyConfiguration: EmailConfiguration = _.cloneDeep(configuration);

    onNext(copyConfiguration);
  };

  // Make the API Call to Save the Configuration
  const getCNameRecords = (domain: string) => async (): Promise<void> => {
    try {
      const api = new API(gatewayEndPoint);

      // Get CName Records for the Domain
      const cnames: DomainCName[] = await api.correspondence.getCNameRecordsV1(domain);

      setcNameRecordsError(false);
      setcNameRecords(cnames);
    } catch (error) {
      const notificationData: NotificationData = {
        type: 'Error',
        title: 'CNAME Records Read Error',
        description: `Unable to Get CNAME Records for '${domain}'`,
        error: error as ResponseError,
      };

      setcNameRecordsError(true);
      setNotification(notificationData);
    }
  };

  // eslint-disable-next-line arrow-body-style
  const renderRowOld = (index: number, left: string, right: string): JSX.Element => {
    return (
      <div className="divTableRow" key={index}>
        <div className="divTableCell">
          CNAME
        </div>
        <div className="divTableCell">
          {left}
        </div>
        <div className="divTableCell">
          {right ? 'CNAME' : ''}
        </div>
        <div className="divTableCell">
          {right || ''}
        </div>
      </div>
    );
  };

  // Render the CNAME Records
  const renderCNames = (): JSX.Element[] => {
    Logging.Info('CNAMEs', cNameRecords);

    // eslint-disable-next-line arrow-body-style
    return cNameRecords?.map((cname: DomainCName, index: number) => {
      const key = index.toString();

      // eslint-disable-next-line arrow-body-style
      return (
        <div className="divTableRow" key={key}>
          <div className="divTableCell">
            {cname.name}
          </div>
          <div className="divTableCell">
            {cname.type}
          </div>
          <div className="divTableCell">
            {cname.value}
          </div>
        </div>
      );
    });
  };

  // eslint-disable-next-line arrow-body-style
  const combineCName = (name: string, type: string, value: string, paddingName: number, paddingType: number, fill: string): string => {
    return `${name.padEnd(paddingName, fill)}${type.padEnd(paddingType, fill)}${value}`;
  };

  // Combine the CNAME Records
  const combineCNames = (): string => {
    let longestName = 0;
    let longestType = 0;

    cNameRecords.forEach((cname: DomainCName) => {
      if (cname.name.length > longestName) {
        longestName = cname.name.length;
      }

      if (cname.type.length > longestType) {
        longestType = cname.type.length;
      }
    });

    longestName += 5;
    longestType += 5;

    const cnames: string[] = [];
    cnames.push(combineCName('NAME', 'TYPE', 'VALUE ', longestName, longestType, ' '));
    cnames.push('');

    // eslint-disable-next-line arrow-body-style
    cNameRecords.forEach((cname: DomainCName) => {
      cnames.push(combineCName(cname.name, cname.type, cname.value, longestName, longestType, ' '));
    });

    return cnames.join('\r\n');
  };

  // Download the CNAME Records to a File
  const onDownloadCNameRecords = (): void => {
    downloadTextFile('cname.txt', combineCNames());
  };

  // Close the Notification
  const onNotificationClose = (): void => {
    setNotification(null);
  };

  React.useEffect(() => {
    // Load the CNAME Records for the new Domain
    if (!cNameRecords) {
      dispatch(getCNameRecords(configuration.domain));
    }

    return (): void => {
      // This Code will run once the component unmounts.
      setcNameRecords(null);
      setNotification(null);
    };
  }, []);

  const backVisible = true;
  const backEnabled = true;
  const cancelVisible = true;
  const cancelEnabled = true;
  const nextVisible = true;
  const nextEnabled = !cNameRecordsError;
  const nextText = 'Finish';

  return (
    <>
      <div className="stepContent">
        {
          !cNameRecords
            ? (
              <>
                {
                  cNameRecordsError
                    ? (
                      <>
                        <div className="descriptionHeading">
                          DNS Records Error
                        </div>
                        <div className="descriptionText errorText">
                          {`Unable to get the CNAME records for the Domain Name '${configuration.domain}'.`}
                        </div>
                      </>
                    )
                    : (
                      <ContentLoading loadingMessage="Loading CNAME Records ..." />
                    )
                }
              </>
            )
            : (
              <>
                <div className="descriptionHeading">
                  Install DNS Records
                </div>
                <div className="descriptionText">
                  {/* eslint-disable-next-line max-len */}
                  DKIM Settings for your domain have been generated. The information below must be added to your domain’s DNS records. How you update the DNS settings depends on who provides your DNS service; If your DNS service is provided by a domain name registrar, please contact that registrar to update your DNS records.
                </div>
                <br />
                <br />
                <div className="divTable">
                  <div className="divTableHeading">
                    <div className="divTableRow">
                      <div className="divTableHead cNameNameCell">
                        Name
                      </div>
                      <div className="divTableHead cNameTypeCell">
                        Type
                      </div>
                      <div className="divTableHead cNameValueCell">
                        Value
                      </div>
                    </div>
                  </div>
                  <div className="divTableBody">
                    {renderCNames()}
                  </div>
                </div>
                <br />
                <div className="downloadCNames">
                  <VtxButton
                    id="downloadButton"
                    onClick={onDownloadCNameRecords}
                    type="link"
                  >
                    Click Here
                  </VtxButton>
                  To Download CNAME Records
                </div>
              </>
            )
        }
      </div>
      <Footer
        onBack={onBack}
        backVisible={backVisible}
        backEnabled={backEnabled}
        onCancel={onCancel}
        cancelVisible={cancelVisible}
        cancelEnabled={cancelEnabled}
        onNext={saveConfiguration}
        nextVisible={nextVisible}
        nextEnabled={nextEnabled}
        nextText={nextText}
      />
      <Notification
        data={notification}
        onNotificationClose={onNotificationClose}
        autoClose
      />
    </>
  );
};

export default Step3;
